import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import React from 'react';
import { v4 } from 'uuid';
import { cloneDeep } from 'lodash';
import { CadNextAction } from '../../action-broadcaster/types';
import { useIntegratedActionBroadcaster } from '../../action-broadcaster/use-integrated-action-broadcaster';
import { isValidLatLng } from '../helpers';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useLocation } from 'react-router-dom';
import { MAP_WINDOW_PATH } from '../../router/constants';
import {
    ViewAmlCallOnMapArgs,
    ViewEntityLocationOnMapArgs,
    ViewGeoPointLocationOnMapArgs,
    ViewGeoPointOnMapArgs,
    ViewLocationOnMapArgs,
    ViewPhoneCallOnMapArgs,
} from './types';

/**
 * entity location args provides entityId and entityType (Unit or Event),
 * we can find the actual entity location by entityId from mapStore.
 */
function isEntityLocationArgs(args: ViewLocationOnMapArgs): args is ViewEntityLocationOnMapArgs {
    return (
        (args as ViewEntityLocationOnMapArgs).entityId !== undefined &&
        (args as ViewEntityLocationOnMapArgs).entityType !== undefined
    );
}
/**
 * geo point location args is a 'location' (BE location entity),
 * it contains all the location fields, usually it's from location select, or from location api returns.
 */
function isGeoPointLocationArgs(
    args: ViewLocationOnMapArgs
): args is ViewGeoPointLocationOnMapArgs {
    return (args as ViewGeoPointLocationOnMapArgs).geoPointLocation !== undefined;
}
/**
 * geo point args only has basic lat/lng info,
 * we can only get the lat/lng and an optional 'fullAddress' string.
 * in most cases, we may just need the lat/lng to move the map center and show 'fullAddress' string as location details.
 * However, in some special cases, we will need to search the reverse geocode location from lat/lng/fullAddress string
 * (e.g. nearby cation address is a string, we need to search geocode location from address string when view nearby caution on map)
 *
 * A 'special' special case: nearby cation address may be a `lat,lng` string when `locations.use_latlong_as_verified.enabled = true`,
 * when view this type nearby cation address on map, set `preferLatLngSourceLocation` will prefer LAT_LONG location from reverse geocode location search.
 */
function isGeoPointArgs(args: ViewLocationOnMapArgs): args is ViewGeoPointOnMapArgs {
    return (
        (args as ViewGeoPointOnMapArgs).latitude !== undefined &&
        (args as ViewGeoPointOnMapArgs).longitude !== undefined
    );
}
/**
 * view ani/ali location on map
 */
function isPhoneCallArgs(args: ViewLocationOnMapArgs): args is ViewPhoneCallOnMapArgs {
    return (args as ViewPhoneCallOnMapArgs).phoneCallView !== undefined;
}
/**
 * view rapidSOS location on map
 */
function isAmlCallArgs(args: ViewLocationOnMapArgs): args is ViewAmlCallOnMapArgs {
    return (args as ViewAmlCallOnMapArgs).amlCallView !== undefined;
}

function getViewLocationOnMapActions(
    args: ViewLocationOnMapArgs,
    targetWindowType: 'ROOT' | 'MAP_V2'
): CadNextAction[] {
    // we should check phone call and AML call args first, as they may also have latitude/longitude properties that would incorrectly match the more generic geo point checks
    if (isPhoneCallArgs(args)) {
        return [
            {
                id: v4(),
                targetWindowType,
                type: 'VIEW_PHONE_CALL_ON_MAP',
                payload: {
                    phoneCallView: args.phoneCallView,
                    location: args.location,
                    shouldClearAmlCall: args.shouldClearAmlCall,
                    shouldMoveMapCenterToLoggedInUnit: args.shouldMoveMapCenterToLoggedInUnit,
                },
            } as CadNextAction,
        ];
    }

    if (isAmlCallArgs(args)) {
        return [
            {
                id: v4(),
                targetWindowType,
                type: 'VIEW_AML_CALL_ON_MAP',
                payload: {
                    amlCallView: args.amlCallView,
                    location: args.location,
                    shouldFocusAml: args.shouldFocusAml,
                    shouldClearPhoneCall: args.shouldClearPhoneCall,
                    shouldMoveMapCenterToLoggedInUnit: args.shouldMoveMapCenterToLoggedInUnit,
                },
            } as CadNextAction,
        ];
    }

    if (isEntityLocationArgs(args) && args.entityId > 0) {
        return [
            {
                id: v4(),
                targetWindowType,
                type: 'VIEW_ENTITY_LOCATION_ON_MAP',
                payload: {
                    entityId: args.entityId,
                    entityType: args.entityType,
                    latitude: args.latitude,
                    longitude: args.longitude,
                    shouldMoveMapCenterToLoggedInUnit: args.shouldMoveMapCenterToLoggedInUnit,
                },
            } as CadNextAction,
        ];
    }

    if (isGeoPointArgs(args) && isValidLatLng(args.latitude, args.longitude)) {
        return [
            {
                id: v4(),
                targetWindowType,
                type: 'VIEW_GEO_POINT_ON_MAP',
                payload: {
                    latitude: args.latitude,
                    longitude: args.longitude,
                    preferLatLngSourceLocation: args.preferLatLngSourceLocation,
                    fullAddress: args.fullAddress,
                    shouldMoveMapCenterToLoggedInUnit: args.shouldMoveMapCenterToLoggedInUnit,
                },
            } as CadNextAction,
        ];
    }

    if (
        isGeoPointLocationArgs(args) &&
        isValidLatLng(args.geoPointLocation.latitude, args.geoPointLocation.longitude)
    ) {
        return [
            {
                id: v4(),
                targetWindowType,
                type: 'VIEW_GEO_POINT_LOCATION_ON_MAP',
                payload: {
                    geoPointLocation: cloneDeep(args.geoPointLocation),
                    shouldRefresh911Calls: args.shouldRefresh911Calls,
                    shouldMoveMapCenterToLoggedInUnit: args.shouldMoveMapCenterToLoggedInUnit,
                },
            } as CadNextAction,
        ];
    }

    return [];
}

export function useViewLocationOnMap(): (args: ViewLocationOnMapArgs) => void {
    const { trigger } = useIntegratedActionBroadcaster();
    const { disableLegacyMapAndSearches, mapWindowEnabled } = useEnabledFeatures();
    const { isCad, isFirstResponder } = useApplicationType();
    const location = useLocation();
    const isOnMap = location.pathname.includes(MAP_WINDOW_PATH);
    const targetWindowTypeForMap = isFirstResponder ? 'ROOT' : 'MAP_V2';

    return React.useCallback(
        (args: ViewLocationOnMapArgs) => {
            const preActions: CadNextAction[] = [];

            if (isCad && (disableLegacyMapAndSearches || mapWindowEnabled)) {
                // OPEN_MAP_V2_WINDOW will always open a map v2 window or bring existing map v2 window to top AND focus on map v2 window.
                // when doNotFocus = true and if map v2 window is not opened, trigger viewLocationOnMap will not open or focus map v2 window.

                if (!args.doNotFocus) {
                    preActions.push({
                        id: v4(),
                        targetWindowType: 'ROOT',
                        type: 'OPEN_MAP_V2_WINDOW',
                    });
                }
            }

            if (isFirstResponder && mapWindowEnabled && !isOnMap) {
                preActions.push({
                    id: v4(),
                    targetWindowType: 'ROOT',
                    type: 'OPEN_FR_MAP_V2_WINDOW',
                });
            }

            trigger([...preActions, ...getViewLocationOnMapActions(args, targetWindowTypeForMap)]);
        },
        [
            trigger,
            disableLegacyMapAndSearches,
            mapWindowEnabled,
            isFirstResponder,
            targetWindowTypeForMap,
            isOnMap,
            isCad,
        ]
    );
}
